import React, { useState, useEffect, useRef, useCallback} from 'react';
import { makeStyles } from '@mui/styles';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Fab from '@mui/material/Fab';
import SendIcon from '@mui/icons-material/Send';
import { useSelector, useDispatch } from 'react-redux';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { api } from 'common';
import {
  Grid,
  Typography,
  Modal,
  Button,
  Tooltip,
  Badge,
  Chip,
  Box
} from '@mui/material';
import { useTranslation } from "react-i18next";
import AlertDialog from '../components/AlertDialog';
import { colors } from 'components/Theme/WebTheme';

const renderFormattedMessageText = (rawText) => {
  if (!rawText || typeof rawText !== 'string') return null;

  // Clean tags and normalize all newline encodings (literal \n and CRLF)
  let text = rawText
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\[CONTACT_OPERATOR\]/g, '')
    .replace(/\[EMERGENCY_SOS\]/g, '')
    .trim();

  // Add line breaks and paragraph separations for Driver Category A Q2:
  // Vehicle documents -> Mandatory vehicle photo -> Photo requirements
  text = text
    .replace(/(?:^|\n*)(?:\*\*)?Vehicle documents\.(?:\*\*)?\s*/i, '**Vehicle documents.** ')
    .replace(/([.!?])\s*(?:\*\*)?Mandatory vehicle photo\.(?:\*\*)?\s*/i, '$1\n\n**Mandatory vehicle photo.** ')
    .replace(/([.!?])\s*(?:\*\*)?Photo requirements\.(?:\*\*)?\s*/i, '$1\n\n**Photo requirements.** ');

  // General rule: If a sentence ends and a bold sub-heading follows without a paragraph break, insert \n\n
  text = text.replace(/([.!?])\s*(\n*\*\*[^*]+?\.\*\*)/g, '$1\n\n$2');

  // Split by double newlines into distinct paragraphs to preserve line breaks and formatting
  const paragraphs = text.split(/\n\s*\n/);

  return paragraphs.map((para, pIdx) => {
    const lines = para.split('\n');
    return (
      <div key={pIdx} style={{ marginBottom: pIdx < paragraphs.length - 1 ? '12px' : 0 }}>
        {lines.map((line, lIdx) => {
          const parts = line.split(/(\*\*[^*]+\*\*)/g);
          return (
            <React.Fragment key={lIdx}>
              {parts.map((part, index) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                  const boldText = part.slice(2, -2);
                  return (
                    <strong key={index} style={{ fontWeight: 700 }}>
                      {boldText}
                    </strong>
                  );
                }
                return part;
              })}
              {lIdx < lines.length - 1 && <br />}
            </React.Fragment>
          );
        })}
      </div>
    );
  });
};


const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 650,
  },
  chatSection: {
    width: '100%',
    height: '85vh'
  },
  headBG: {
      backgroundColor: '#e0e0e0'
  },
  borderRight500: {
      borderRight: '1px solid #e0e0e0'
  },
  messageArea: {
    height: '70vh',
    overflowY: 'auto',
    backgroundColor: '#F8FAFC',
    padding: '16px 20px',
  },
  modal: {
    display: 'flex',
    padding: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    width: 680,
    backgroundColor: theme.palette.background.paper,
    border: '2px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  textField: {
    "& label.Mui-focused": {
        color: colors.MAIN_COLOR,
    },
    "& .MuiInput-underline:after": {
        borderBottomColor: colors.MAIN_COLOR,
    },
    "& .MuiFilledInput-underline:after": {
        borderBottomColor: colors.MAIN_COLOR,
    },
    "& .MuiOutlinedInput-root": {
        "&.Mui-focused fieldset": {
            borderColor: colors.MAIN_COLOR,
        },
    },
},
rootRtl_4: {
  "& label": {
      right: 17,
      left: "auto",
      paddingRight: 12
  },
  "& legend": {
      textAlign: "right",
      marginRight: 15
  },
  "& label.Mui-focused": {
      color: colors.MAIN_COLOR,
  },
  "& .MuiInput-underline:after": {
      borderBottomColor: colors.MAIN_COLOR,
  },
  "& .MuiFilledInput-underline:after": {
      borderBottomColor: colors.MAIN_COLOR,
  },
  "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
          borderColor: colors.MAIN_COLOR,
      },
  },
},
rootRtl: {
  "& label": {
      right: 20,
      left: "auto",
      paddingRight: 20
  },
  "& legend": {
      textAlign: "right",
      marginRight: 15
  },
  "& label.Mui-focused": {
      color: colors.MAIN_COLOR,
  },
  "& .MuiInput-underline:after": {
      borderBottomColor: colors.MAIN_COLOR,
  },
  "& .MuiFilledInput-underline:after": {
      borderBottomColor: colors.MAIN_COLOR,
  },
  "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
          borderColor: colors.MAIN_COLOR,
      },
  },
},
}));

const Chat = () => {

  const { t, i18n  } = useTranslation();
  const isRTL = i18n.dir();
  const rootRef = useRef(null);

  const classes = useStyles();
  const admchatdata = useSelector(state=>state.admchatdata);
  const {
    sendAdmMessage,
    editChat,
    updateChatStatus
  } = api;
  const [userId, setUserId] = useState();
  const [messages, setMessages] = useState();
  const [msg,setMsg] = useState("");
  const [searchText, setSearchText] = useState();
  const scrollMsgRef = useRef();
  const scrollChtRef = useRef();
  const dispatch = useDispatch();
  const [open,setOpen] = useState(false);
  const [chat,setchat] = useState();
  const [chats,setChats] = useState();
  const [reads,setReads] = useState({});
  const [firstTime, setFirstTime] = useState(true)
  const settings = useSelector(state => state.settingsdata.settings);
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });

  useEffect(()=>{
    if(admchatdata.chats){
      setChats(admchatdata.chats.map(chat => ({...chat, messages: chat.messages ? [...chat.messages] : []})));
    }
    if(admchatdata.reads){
      setReads(admchatdata.reads);
    }
  },[admchatdata]);

  const sendMessage = () => {
    if (!userId || typeof userId !== 'string' || !userId.trim()) {
      return;
    }
    if(settings.AllowCriticalEditsAdmin){
      dispatch(sendAdmMessage({
        userId: userId.trim(),
        message: msg,
        from: 'admin'
      }));
      setMsg("");
      
    }else{
      setCommonAlert({ open: true, msg: t('demo_mode') });
    }


  };

  const handleAdminEndChat = () => {
    if (!userId || typeof userId !== 'string' || !userId.trim()) return;
    if (settings.AllowCriticalEditsAdmin) {
      if (updateChatStatus) {
        dispatch(updateChatStatus(userId.trim(), 'Ended'));
      }
      dispatch(sendAdmMessage({
        userId: userId.trim(),
        message: t('operator_ended_session', 'The operator has ended the support chat session.'),
        from: 'admin'
      }));
    } else {
      setCommonAlert({ open: true, msg: t('demo_mode') });
    }
  };

  const handleClose = () => {
    setOpen(false);
  }

  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: '' })
  };
  
  const deleteChar = () => {
    if(settings.AllowCriticalEditsAdmin){
      dispatch(editChat(chat,"Delete"));
      setOpen(false);
      alert(t('chat_delete_succes'));
      if(chats && chats.length === 1 ){
        setTimeout(() =>{
          setChats([]);
          setMessages([]);
        },100);
      }
    }else{
      setCommonAlert({ open: true, msg: t('demo_mode') });
    }
   
  }

  const scrollToBottom = () => {
    if (scrollMsgRef.current) {
      const scrollHeight = scrollMsgRef.current.scrollHeight;
      scrollMsgRef.current.scrollTop = scrollHeight;
      scrollChtRef.current.scrollTop = 0;
    }
  };

  const makeRead = useCallback((userId) => {
      if (userId && typeof userId === 'string' && userId.trim()) {
        dispatch(editChat({userId: userId.trim()},"Read"))   
      }
  },[dispatch, editChat]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && /\S/.test(msg)) {
      sendMessage();
    }
  };

  useEffect(()=>{
    if(chats && chats.length > 0){
        let sorted = [...chats].sort((a,b)=>b.lastUpdateTime - a.lastUpdateTime)
        if(sorted.length>0 && firstTime){
            const firstUid = sorted[0].userId || sorted[0].id;
            if (firstUid && typeof firstUid === 'string' && firstUid.trim()) {
              setUserId(firstUid.trim());
              makeRead(firstUid.trim());
            }
            setMessages([...(sorted[0].messages || [])].sort((a,b)=>a.createdAt - b.createdAt));
            setTimeout(()=>{
                scrollToBottom();
            }, 200); 
            setFirstTime(false)
        }
 
    }
  },[chats, searchText, dispatch, editChat, makeRead, firstTime]);

  useEffect(()=>{
    if(userId && chats && chats.length>0){
      let sorted = [...chats].sort((a,b)=>b.lastUpdateTime - a.lastUpdateTime)
      let selectedUserMsg = sorted.filter((item)=>{
        return (item.userId || item.id) === userId
      })
      const activeUid = selectedUserMsg[0]?.userId || selectedUserMsg[0]?.id || userId;
      if (activeUid && typeof activeUid === 'string' && activeUid.trim()) {
        makeRead(activeUid.trim());
      }
      setMessages([...(selectedUserMsg[0]?.messages || [])].sort((a,b)=>a.createdAt - b.createdAt))
      setTimeout(()=>{
        scrollToBottom();
    }, 200); 
    }
  },[chats, userId, makeRead])
  

  const openChat = (item) => {
    const uid = item?.userId || item?.id;
    if (uid && typeof uid === 'string' && uid.trim()) {
      setUserId(uid.trim());
      makeRead(uid.trim());
    }
    setMessages([...(item?.messages || [])].sort((a,b)=>a.createdAt - b.createdAt));
    setTimeout(()=>{
        scrollToBottom();
    }, 200);      
  };

  const openDeleteModel = (item) => {
    setchat(item)
    setOpen(true);
  };

  return (
    chats&& chats.length > 0 ? (
      <div>
        <Grid container>
            <Grid item xs={12} >
                <Typography variant="h5" className="header-message">{ t('chat')}</Typography>
            </Grid>
        </Grid>
        <Grid item xs={12} sm={12}>
      <Grid
        container spacing={2}
      >
        <Grid item xs={12} sm={12} md={4} lg={4} className={classes.borderRight500}>
                <Divider />
                <Grid item xs={12} style={{padding: '10px'}}>
                    <TextField id="outlined-basic-email" label={t("search")} variant="outlined" fullWidth  className={isRTL === "rtl" ? classes.rootRtl : classes.textField}
                        onChange={(e) => {
                            setSearchText(e.target.value);
                        }}
                    />
                </Grid>
                <Divider />
                <List ref={scrollChtRef}>
                    {chats && chats.length> 0 && [...chats].sort((a,b)=>b.lastUpdateTime - a.lastUpdateTime).filter(item => (!searchText || searchText === "" || (item.userName && item.userName.toLowerCase().includes(searchText.toLowerCase())))).map((item,index)=>{
                        const isOpRequested = Boolean(item.operatorRequested || item.status === 'Operator Requested');
                        const itemUid = item.userId || item.id;
                        return (
                        <ListItem 
                            button 
                            key={"key" + index} 
                            onClick={()=>openChat(item)} 
                            style={{
                              backgroundColor: userId === itemUid ? '#EDF2F7' : '#FFFFFF',
                              borderBottom: '1px solid #F1F5F9',
                              padding: '12px 14px'
                            }}
                        >
                            <ListItemIcon style={{ minWidth: 48 }}>
                                <Avatar alt={item.userName} src={item.profileImage} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={
                                <Typography style={{ fontWeight: userId === itemUid ? 700 : 500, fontSize: '0.92rem', color: '#1E293B' }}>
                                  {item.userName}
                                </Typography>
                              }
                              secondary={
                                isOpRequested ? (
                                  <span style={{ color: '#D97706', fontWeight: 700, fontSize: '0.78rem' }}>
                                    {t('operator_requested', 'Operator Requested')}
                                  </span>
                                ) : (reads[itemUid] ? t('unread') : (item.status ? item.status : ''))
                              }
                            />
                            {isOpRequested && (
                              <Tooltip title={t('operator_requested', 'Operator Requested')}>
                                <Badge color="error" variant="dot" style={{ marginRight: 10 }}>
                                  <NotificationsActiveIcon style={{ color: '#ff9800', fontSize: 20 }} />
                                </Badge>
                              </Tooltip>
                            )}
                            <DeleteIcon 
                              onClick={(e)=>{
                                e.stopPropagation();
                                openDeleteModel(item);
                              }} 
                              style={{ color: '#94A3B8', cursor: 'pointer', fontSize: 20 }}
                            />
                        </ListItem>
                        );
                    })}
                </List>
            </Grid>
            <Grid item xs={12} sm={12} md={8} lg={8} >
            <Divider />
            {chats && chats.length > 0 && (() => {
              const activeChat = chats.find(c => (c.userId || c.id) === userId);
              if (!activeChat) return null;
              const isOpRequested = Boolean(activeChat.operatorRequested || activeChat.status === 'Operator Requested');
              const isChatActive = activeChat.status === 'Active';
              const canEndSession = isOpRequested || isChatActive;
              return (
                <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar alt={activeChat.userName} src={activeChat.profileImage} style={{ width: 36, height: 36 }} />
                    <Box>
                      <Typography style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1E293B' }}>
                        {activeChat.userName || t('user_label', 'User')}
                      </Typography>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                        {isOpRequested ? (
                          <Chip
                            size="small"
                            label={t('operator_requested', 'Operator Requested')}
                            style={{ backgroundColor: '#FEF3C7', color: '#B45309', fontWeight: 700, fontSize: '0.72rem', height: 20 }}
                          />
                        ) : isChatActive ? (
                          <Chip
                            size="small"
                            label={t('live_operator_active', 'Live Operator Active')}
                            style={{ backgroundColor: '#DCFCE7', color: '#15803D', fontWeight: 700, fontSize: '0.72rem', height: 20 }}
                          />
                        ) : activeChat.status === 'Ended' ? (
                          <Chip
                            size="small"
                            label={t('ended', 'Ended')}
                            style={{ backgroundColor: '#F1F5F9', color: '#64748B', fontWeight: 600, fontSize: '0.72rem', height: 20 }}
                          />
                        ) : null}
                      </Box>
                    </Box>
                  </Box>
                  {canEndSession && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAdminEndChat}
                      style={{
                        backgroundColor: '#DC2626',
                        color: '#FFFFFF',
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        borderRadius: 6,
                        padding: '4px 12px'
                      }}
                    >
                      {t('end_chat_btn', 'End Chat')}
                    </Button>
                  )}
                </Box>
              );
            })()}
                <List ref={scrollMsgRef} className={classes.messageArea}>
                    {messages && messages.length> 0 && messages.map((item,index)=>{
                      const isAdmin = item.from === 'admin';
                      const isBot = item.from === 'support_bot';

                      const showDateHeader = index === 0 || (index > 0 && (new Date(messages[index].createdAt).getUTCDate()) !== (new Date(messages[index - 1].createdAt).getUTCDate()));

                      return (
                        <React.Fragment key={"key" + index}>
                          {showDateHeader && (
                            <Box style={{ width: '100%', textAlign: 'center', margin: '14px 0 10px' }}>
                              <Chip 
                                size="small" 
                                label={new Date(item.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                style={{ backgroundColor: '#E2E8F0', color: '#475569', fontSize: '0.75rem', fontWeight: 600 }}
                              />
                            </Box>
                          )}

                          <ListItem 
                            disableGutters 
                            style={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              alignItems: isAdmin ? 'flex-end' : 'flex-start',
                              padding: '6px 0',
                              width: '100%'
                            }}
                          >
                            {/* Role Label Above Message */}
                            {isAdmin ? (
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 5,
                                  padding: '2px 9px',
                                  borderRadius: 12,
                                  backgroundColor: '#DCFCE7',
                                  color: '#166534',
                                  border: '1px solid #86EFAC',
                                  fontSize: '0.74rem',
                                  fontWeight: 700,
                                  marginBottom: 4,
                                  boxShadow: '0 1px 2px rgba(16,185,129,0.06)'
                                }}
                              >
                                <span style={{ fontSize: '0.85rem' }}>🛡️</span>
                                <span>{t('support_agent_label', 'Support Agent')}</span>
                              </div>
                            ) : isBot ? (
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 5,
                                  padding: '2px 9px',
                                  borderRadius: 12,
                                  backgroundColor: '#EEF2FF',
                                  color: '#4338CA',
                                  border: '1px solid #C7D2FE',
                                  fontSize: '0.74rem',
                                  fontWeight: 700,
                                  marginBottom: 4,
                                  boxShadow: '0 1px 2px rgba(99,102,241,0.08)'
                                }}
                              >
                                <span style={{ fontSize: '0.9rem' }}>🤖</span>
                                <span>{t('virtual_assistant_label', 'Virtual Assistant')}</span>
                              </div>
                            ) : (
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 5,
                                  padding: '2px 9px',
                                  borderRadius: 12,
                                  backgroundColor: '#F1F5F9',
                                  color: '#334155',
                                  border: '1px solid #CBD5E1',
                                  fontSize: '0.74rem',
                                  fontWeight: 700,
                                  marginBottom: 4,
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                                }}
                              >
                                <span style={{ fontSize: '0.85rem' }}>👤</span>
                                <span>{t('user_label', 'User')}</span>
                              </div>
                            )}

                            {/* Message Card Bubble */}
                            <div
                              style={
                                isAdmin
                                  ? {
                                      maxWidth: '78%',
                                      backgroundColor: '#ECFDF5',
                                      border: '1.5px solid #A7F3D0',
                                      borderRadius: '16px 16px 4px 16px',
                                      padding: '12px 16px',
                                      boxShadow: '0 2px 6px rgba(16, 185, 129, 0.08)',
                                      color: '#064E3B',
                                      fontSize: '0.93rem',
                                      lineHeight: 1.55,
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                    }
                                  : isBot
                                  ? {
                                      maxWidth: '82%',
                                      backgroundColor: '#FFFFFF',
                                      border: '1.5px solid #C7D2FE',
                                      borderRadius: '16px 16px 16px 4px',
                                      padding: '14px 18px',
                                      boxShadow: '0 3px 8px rgba(99, 102, 241, 0.08)',
                                      color: '#0F172A',
                                      fontSize: '0.93rem',
                                      lineHeight: 1.6,
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                    }
                                  : {
                                      maxWidth: '78%',
                                      backgroundColor: '#FFFFFF',
                                      border: '1px solid #E2E8F0',
                                      borderRadius: '16px 16px 16px 4px',
                                      padding: '12px 16px',
                                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
                                      color: '#1E293B',
                                      fontSize: '0.93rem',
                                      lineHeight: 1.55,
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                    }
                              }
                            >
                              {renderFormattedMessageText(item.text)}
                            </div>

                            {/* Timestamp */}
                            <div
                              style={{
                                fontSize: '0.72rem',
                                color: '#94A3B8',
                                marginTop: 3,
                                padding: '0 4px',
                                textAlign: isAdmin ? 'right' : 'left'
                              }}
                            >
                              {new Date(item.createdAt).toLocaleTimeString().replace(/(.*)\D\d+/, '$1')}
                            </div>
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                </List>
                <Divider />
                <Grid container style={{padding: '20px'}}>
                    <Grid item xs={11}>
                        <TextField id="outlined-basic-email" label={t("type_something")}  className={isRTL === "rtl" ? classes.rootRtl_4 : classes.textField} fullWidth 
                          onChange={(e) => {
                            setMsg(e.target.value);
                          }}
                          onKeyDown={handleKeyDown}
                          value={msg}
                        />
                    </Grid>
                    <Grid item xs={1} align="right">
                    <Fab color="primary" aria-label="add" disabled={(chats && chats.length > 0) && !/\S/.test(msg)}onClick={sendMessage}><SendIcon /></Fab>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
        </Grid>

        <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        onClose={handleClose}
        open={open}
        className={classes.modal}
        container={() => rootRef.current}
      >
        <div className={classes.paper}>
          <Grid container spacing={2} >

            <Grid item xs={12}>
              <Typography component="h2" variant="h6" className={classes.title} style={{textAlign:isRTL=== 'rtl' ? 'right' : 'left'}}>
                {t('delete_chat')}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} style={{direction: isRTL=== 'rtl' ?'rtl' : 'ltr', marginLeft:isRTL=== 'rtl' ? '65%' : 0}}>
            <Button onClick={handleClose} variant="contained" color="primary">
              {t('no')}
            </Button>
            <Button onClick={deleteChar} variant="contained" color="primary" style={ isRTL=== 'rtl' ? {marginRight:10} : {marginLeft:10}}>
              {t('yes')}
            </Button>
          </Grid>
          </Grid>
        </div>
      </Modal>

      <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>{commonAlert.msg}</AlertDialog>
      </div>
      ) : 
      <div>
         <Grid container justifyContent="center" alignItems="center" style={{ height: '80vh', textAlign: 'center' }}>
            <Typography variant="h5" style={{ marginTop: '20px' }}>{t('noChatMessage')} </Typography> 
          </Grid> 
      </div>
  );
}

export default Chat;